import * as stylex from "@stylexjs/stylex";
import { ChevronDown } from "lucide-react";
import type { Ref } from "react";
import type { Tag } from "@/domain/schemas";
import { initials, stackCircles } from "@/domain/tags";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import { Icon } from "./Icon";
import { press } from "./press";
import { tagFills } from "./tagFills";
export type WhoStackSize = "large" | "small" | "today";
type WhoStackProps = {
  shown: readonly Tag[];
  max: number;
  size: WhoStackSize;
  label: string;
  name: string;
  more: (count: number) => string;
  onOpen: () => void;
  ref?: Ref<HTMLButtonElement>;
};
const HEIGHT: Record<WhoStackSize, number> = {
  large: 48,
  small: 44,
  today: 48
};
const _temp = {
  ks0D6T: "x193iq5w",
  k7Eaqz: "xeuugli",
  kmuXW: "xs83m0k",
  kSGwAc: "xqcrz7y",
  k1xSpc: "x78zum5",
  kGNEyG: "x6s0dn4",
  kOIVth: "x1v2ro7d",
  k8WAf4: "xt970qd",
  kZCmMZ: "x1c1uobl",
  kwRFfy: "x11lfxj5",
  kMzoRj: "xc342km",
  kWkggS: "xjbqb8w",
  kMwMTN: "x1a11ngh",
  kMv6JI: "xjb2p0i",
  kGuDYH: "x19d36u7",
  k63SB2: "x1cf7cre",
  kkrTdU: "x1ypdohk",
  k3nNDw: "x1nf803f",
  k1ekBW: "x11xpdln",
  kIyJzY: "x1bpocu2",
  kAMwcw: "x1em5f5i",
  "$$css": true
};
const styles = {
  button: (minHeight: number) => [_temp, {
    kAzted: minHeight != null ? "x82snj4" : minHeight,
    $$css: true
  }, {
    "--x-minHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(minHeight)
  }],
  circle: {
    kVAEAm: "x1n2onr6",
    kmuXW: "x2lah0s",
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    keTefX: "x165d6jo",
    kaIpWk: "xqhrb7l",
    kGVxlE: "xjmit6t",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kGuDYH: "x1jvydc1",
    k63SB2: "xfbjfs5",
    kcqcaj: "xss6m8b",
    $$css: true
  },
  today: {
    kzqmXN: "x100vrsf",
    kZKoxP: "x1vqgdyp",
    keTefX: "x18xomjl",
    kGuDYH: "xif65rj",
    $$css: true
  },
  first: {
    keTefX: "x1lziwak",
    $$css: true
  },
  order: (zIndex: number) => [{
    kY2c9j: zIndex != null ? "xr3buco" : zIndex,
    $$css: true
  }, {
    "--x-zIndex": zIndex != null ? zIndex : undefined
  }],
  nobody: {
    kWkggS: "x1wf3224",
    $$css: true
  },
  more: {
    kWkggS: "x5r8ks9",
    $$css: true
  }
};
const MORE_LAYER = 20;
export function WhoStack({
  shown,
  max,
  size,
  label,
  name,
  more,
  onOpen,
  ref
}: WhoStackProps) {
  const stack = stackCircles(shown, max);
  const today = size === "today";
  const circle = (index: number) => [styles.circle, today && styles.today, index === 0 && styles.first, styles.order(index + 1)];
  return <button ref={ref} type="button" aria-haspopup="dialog" aria-label={name} onClick={onOpen} {...stylex.props(styles.button(HEIGHT[size]), press.scale)}>
      <span aria-hidden="true" className="x2lah0s x78zum5 x6s0dn4 xyiysdx xc8icb0">
        {shown.length === 0 && <span {...stylex.props(circle(0), styles.nobody)} />}
        {stack.shown.map((tag, index) => <span key={tag.id} {...stylex.props(circle(index), tagFills[tag.color])}>
            {!today && initials(tag.name)}
          </span>)}
        {stack.more > 0 && <span {...stylex.props(circle(stack.shown.length), styles.order(MORE_LAYER), styles.more)}>
            {more(stack.more)}
          </span>}
      </span>
      <span className="xeuugli xuxw1ft xb3r6kr xlyipyv">{label}</span>
      <Icon icon={ChevronDown} size={18} strokeWidth={2} />
    </button>;
}